import { BasicButton } from '../../../widgets/buttons';
import { Dialog } from '../../../widgets/dialog';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { T, toGlobalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { ImageLightbox } from '../components/layout/imageLightbox';
import { DialogContent, DialogFooter } from '../components/shared/sharedStyles';
import { aiPresetQuery } from '../queries/__generated__/aiPresetQuery.graphql';
import { AIPresetQuery } from '../queries/aiPreset';

const ScrollableContent = styled.div`
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 4px;
`;

const Section = styled.div`
    &:not(:last-child) {
        margin-bottom: 20px;
        padding-bottom: 20px;
        border-bottom: 1px solid #e0e0e0;
    }
`;

const SectionTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #78909c;
    margin-bottom: 12px;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const InfoCard = styled.div<{ $fullWidth?: boolean }>`
    background: #f6f8f9;
    border-radius: 8px;
    padding: 10px 12px;
    grid-column: ${(props) => (props.$fullWidth ? '1 / -1' : 'auto')};
`;

const InfoLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #78909c;
    margin-bottom: 4px;
`;

const InfoValue = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.app.textColor};
    word-break: break-word;
    line-height: 1.4;
`;

const PromptValue = styled(InfoValue)`
    white-space: pre-wrap;
    max-height: 120px;
    overflow-y: auto;
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
`;

const ImageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ImageWrapper = styled.div`
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    background: #f0f0f0;
    border: 1px solid #e0e0e0;
    aspect-ratio: 1;
    cursor: pointer;
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }
`;

const ImageThumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const ImageLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #78909c;
    text-align: center;
`;

const BooleanBadge = styled.span<{ $active: boolean }>`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: ${(props) => (props.$active ? '#e8f5e9' : '#fbe9e7')};
    color: ${(props) => (props.$active ? '#2e7d32' : '#c62828')};
`;

const PresetGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
`;

const PresetCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: #f6f8f9;
    border-radius: 8px;
    padding: 8px;
    align-items: center;
`;

const PresetThumbnailWrapper = styled.div`
    width: 100%;
    aspect-ratio: 1;
    border-radius: 6px;
    overflow: hidden;
    background: #e0e0e0;
    cursor: pointer;
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }
`;

const PresetThumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const PresetName = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    text-align: center;
`;

const PresetType = styled.div`
    font-size: 10px;
    color: #78909c;
    text-align: center;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 20px;
    color: #78909c;
    font-size: 13px;
`;

interface MetadataObject {
    readonly [key: string]: string | number | boolean | string[] | readonly string[] | null | undefined;
}

interface AssetInfoDialogProps {
    metadata: MetadataObject | null | undefined;
    assetName: string;
    onClose: () => void;
}

const LABEL_MAP: Record<string, string> = {
    toolName: 'Tool',
    prompt: 'Prompt',
    negativePrompt: 'Negative Prompt',
    model: 'Model',
    width: 'Width',
    height: 'Height',
    seed: 'Seed',
    strength: 'Strength',
    steps: 'Steps',
    guidanceScale: 'Guidance Scale',
    sourceImageUrls: 'Source Image',
    styleImageUrl: 'Style Image',
    maskImageUrl: 'Mask Image',
    backgroundPreset: 'Background',
    backgroundImageUrl: 'Background Image',
    personPreset: 'Person',
    personImageUrl: 'Person Image',
    clothingPreset: 'Clothing',
    clothingImageUrl: 'Clothing Image',
    posePreset: 'Pose',
    effectPreset: 'Effect',
    surfacePreset: 'Surface',
    moodPreset: 'Mood',
    materialPreset: 'Material',
    colorPreset: 'Color',
    layoutPreset: 'Layout',
    formatPreset: 'Format',
    stylePreset: 'Style',
    videoPreset: 'Video',
    scaleFactor: 'Scale Factor',
    durationSeconds: 'Duration (s)',
    resolution: 'Resolution',
    aspectRatio: 'Aspect Ratio',
    generateAudio: 'Generate Audio',
    language: 'Language',
    title: 'Title',
    subtitle: 'Subtitle',
    ctaText: 'CTA Text',
    savedAt: 'Saved At'
};

const IMAGE_KEYS = new Set(['sourceImageUrls', 'styleImageUrl', 'maskImageUrl', 'backgroundImageUrl', 'personImageUrl', 'clothingImageUrl']);

const PRESET_KEYS = new Set([
    'backgroundPreset',
    'personPreset',
    'clothingPreset',
    'posePreset',
    'effectPreset',
    'surfacePreset',
    'moodPreset',
    'materialPreset',
    'colorPreset',
    'layoutPreset',
    'formatPreset',
    'stylePreset',
    'videoPreset'
]);

const PROMPT_KEYS = new Set(['prompt', 'negativePrompt']);

const GENERAL_KEYS = new Set(['toolName', 'model', 'savedAt']);

const HIDDEN_KEYS = new Set(['toolId']);

const DIMENSION_KEYS = new Set(['width', 'height', 'resolution', 'aspectRatio', 'scaleFactor', 'durationSeconds']);

const PARAMETER_KEYS = new Set(['seed', 'strength', 'steps', 'guidanceScale']);

const isImageValue = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return value.startsWith('data:image/') || /^https?:\/\/.+/i.test(value);
};

interface PresetInfo {
    id: string;
    name: string;
    previewUrl: string | null;
}

interface ResolvedPreset {
    key: string;
    label: string;
    presetId: string;
    name: string;
    previewUrl: string | null;
}

export const AssetInfoDialog: FC<AssetInfoDialogProps> = ({ metadata, assetName, onClose }) => {
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
    const [lightboxAlt, setLightboxAlt] = useState<string | undefined>();
    const [resolvedPresets, setResolvedPresets] = useState<ResolvedPreset[]>([]);
    const [presetsLoading, setPresetsLoading] = useState(false);
    const environment = useRelayEnvironment();

    const presetEntries = useMemo(() => {
        if (!metadata) return [];
        const entries: { key: string; label: string; presetId: string }[] = [];
        for (const [key, value] of Object.entries(metadata)) {
            if (value == null || value === '') continue;
            if (PRESET_KEYS.has(key)) {
                entries.push({ key, label: LABEL_MAP[key] || key, presetId: String(value) });
            }
        }
        return entries;
    }, [metadata]);

    useEffect(() => {
        if (presetEntries.length === 0) return;

        let cancelled = false;
        setPresetsLoading(true);

        const fetchPresets = async () => {
            const results: ResolvedPreset[] = [];

            await Promise.all(
                presetEntries.map(async (entry) => {
                    try {
                        const data = await fetchQuery<aiPresetQuery>(environment, AIPresetQuery, {
                            id: toGlobalID(entry.presetId, 'AIPresetDTO')
                        }).toPromise();

                        if (cancelled) return;

                        if (data?.aiPreset) {
                            const preset: PresetInfo = data.aiPreset;
                            results.push({
                                key: entry.key,
                                label: entry.label,
                                presetId: entry.presetId,
                                name: preset.name,
                                previewUrl: preset.previewUrl
                            });
                        }
                    } catch {
                        if (!cancelled) {
                            results.push({
                                key: entry.key,
                                label: entry.label,
                                presetId: entry.presetId,
                                name: entry.presetId,
                                previewUrl: null
                            });
                        }
                    }
                })
            );

            if (!cancelled) {
                setResolvedPresets(results);
                setPresetsLoading(false);
            }
        };

        fetchPresets();
        return () => {
            cancelled = true;
        };
    }, [presetEntries, environment]);

    const { general, prompts, images, dimensions, parameters, otherSettings } = useMemo(() => {
        const general: { label: string; value: string; key: string }[] = [];
        const prompts: { label: string; value: string; key: string }[] = [];
        const images: { label: string; value: string; key: string }[] = [];
        const dimensions: { label: string; value: string; key: string }[] = [];
        const parameters: { label: string; value: string; key: string }[] = [];
        const otherSettings: { label: string; value: string; key: string }[] = [];

        if (!metadata) return { general, prompts, images, dimensions, parameters, otherSettings };

        for (const [key, value] of Object.entries(metadata)) {
            if (value == null || value === '') continue;
            if (HIDDEN_KEYS.has(key)) continue;
            if (PRESET_KEYS.has(key)) continue;

            // Handle sourceImageUrls as an array of URLs
            if (key === 'sourceImageUrls' && Array.isArray(value)) {
                const label = LABEL_MAP[key] || key;
                value.forEach((url, index) => {
                    if (url) {
                        images.push({ label: value.length > 1 ? `${label} ${index + 1}` : label, value: url, key: `${key}_${index}` });
                    }
                });
                continue;
            }

            const label = LABEL_MAP[key] || key;
            let displayValue: string;

            if (key === 'savedAt') {
                try {
                    displayValue = new Date(value as string).toLocaleString();
                } catch {
                    displayValue = String(value);
                }
            } else if (typeof value === 'boolean') {
                displayValue = value ? 'Yes' : 'No';
            } else {
                displayValue = String(value);
            }

            const entry = { label, value: displayValue, key };

            if (IMAGE_KEYS.has(key) || isImageValue(value)) {
                images.push(entry);
            } else if (PROMPT_KEYS.has(key)) {
                prompts.push(entry);
            } else if (GENERAL_KEYS.has(key)) {
                general.push(entry);
            } else if (DIMENSION_KEYS.has(key)) {
                dimensions.push(entry);
            } else if (PARAMETER_KEYS.has(key)) {
                parameters.push(entry);
            } else {
                otherSettings.push(entry);
            }
        }

        return { general, prompts, images, dimensions, parameters, otherSettings };
    }, [metadata]);

    const hasContent =
        general.length > 0 ||
        prompts.length > 0 ||
        images.length > 0 ||
        dimensions.length > 0 ||
        parameters.length > 0 ||
        presetEntries.length > 0 ||
        otherSettings.length > 0;

    const renderValue = (entry: { label: string; value: string; key: string }) => {
        if (typeof metadata?.[entry.key] === 'boolean') {
            return <BooleanBadge $active={metadata[entry.key] as boolean}>{entry.value}</BooleanBadge>;
        }
        return <InfoValue>{entry.value}</InfoValue>;
    };

    const openLightbox = (src: string, alt?: string) => {
        setLightboxSrc(src);
        setLightboxAlt(alt);
    };

    return (
        <Dialog title={`${T._('Info', 'Admin')} - ${assetName}`} onClose={onClose}>
            <DialogContent>
                {hasContent ? (
                    <ScrollableContent>
                        {general.length > 0 && (
                            <Section>
                                <SectionTitle>{T._('General', 'Admin')}</SectionTitle>
                                <InfoGrid>
                                    {general.map((entry) => (
                                        <InfoCard key={entry.key}>
                                            <InfoLabel>{entry.label}</InfoLabel>
                                            {renderValue(entry)}
                                        </InfoCard>
                                    ))}
                                </InfoGrid>
                            </Section>
                        )}

                        {prompts.length > 0 && (
                            <Section>
                                <SectionTitle>{T._('Prompts', 'Admin')}</SectionTitle>
                                {prompts.map((entry, i) => (
                                    <InfoCard key={entry.key} $fullWidth style={{ marginBottom: i < prompts.length - 1 ? 10 : 0 }}>
                                        <InfoLabel>{entry.label}</InfoLabel>
                                        <PromptValue>{entry.value}</PromptValue>
                                    </InfoCard>
                                ))}
                            </Section>
                        )}

                        {images.length > 0 && (
                            <Section>
                                <SectionTitle>{T._('Images', 'Admin')}</SectionTitle>
                                <ImageGrid>
                                    {images.map((entry) => (
                                        <ImageCard key={entry.key}>
                                            <ImageWrapper onClick={() => openLightbox(entry.value, entry.label)}>
                                                <ImageThumbnail src={entry.value} alt={entry.label} />
                                            </ImageWrapper>
                                            <ImageLabel>{entry.label}</ImageLabel>
                                        </ImageCard>
                                    ))}
                                </ImageGrid>
                            </Section>
                        )}

                        {(resolvedPresets.length > 0 || presetsLoading) && (
                            <Section>
                                <SectionTitle>{T._('Presets', 'Admin')}</SectionTitle>
                                {presetsLoading ? (
                                    <InfoValue style={{ color: '#78909c', fontSize: 12 }}>{T._('Loading presets...', 'Admin')}</InfoValue>
                                ) : (
                                    <PresetGrid>
                                        {resolvedPresets.map((preset) => (
                                            <PresetCard key={preset.key}>
                                                {preset.previewUrl && (
                                                    <PresetThumbnailWrapper onClick={() => openLightbox(preset.previewUrl!, preset.name)}>
                                                        <PresetThumbnail src={preset.previewUrl} alt={preset.name} />
                                                    </PresetThumbnailWrapper>
                                                )}
                                                <PresetName>{preset.name}</PresetName>
                                                <PresetType>{preset.label}</PresetType>
                                            </PresetCard>
                                        ))}
                                    </PresetGrid>
                                )}
                            </Section>
                        )}

                        {(dimensions.length > 0 || parameters.length > 0) && (
                            <Section>
                                <SectionTitle>{T._('Parameters', 'Admin')}</SectionTitle>
                                <InfoGrid>
                                    {[...dimensions, ...parameters].map((entry) => (
                                        <InfoCard key={entry.key}>
                                            <InfoLabel>{entry.label}</InfoLabel>
                                            {renderValue(entry)}
                                        </InfoCard>
                                    ))}
                                </InfoGrid>
                            </Section>
                        )}

                        {otherSettings.length > 0 && (
                            <Section>
                                <SectionTitle>{T._('Settings', 'Admin')}</SectionTitle>
                                <InfoGrid>
                                    {otherSettings.map((entry) => (
                                        <InfoCard key={entry.key}>
                                            <InfoLabel>{entry.label}</InfoLabel>
                                            {renderValue(entry)}
                                        </InfoCard>
                                    ))}
                                </InfoGrid>
                            </Section>
                        )}
                    </ScrollableContent>
                ) : (
                    <EmptyState>{T._('No generation info available for this asset.', 'Admin')}</EmptyState>
                )}
                <DialogFooter>
                    <BasicButton onClick={onClose}>{T._('Close', 'Admin')}</BasicButton>
                </DialogFooter>
            </DialogContent>

            {lightboxSrc && <ImageLightbox imageUrl={lightboxSrc} alt={lightboxAlt} onClose={() => setLightboxSrc(null)} showDownload={false} />}
        </Dialog>
    );
};
